/**
 * Retrieve a CSS3 variable value from its name. The variable must be
 * defined in the :root scope like this:
 *
 * @example
 * :root {
 *  --line-chart-border-color: @fp-blue;
 * }
 * @param {string} name - The CSS3 variable name, e.g.,
 *   "--line-chart-border-color"
 */
export function getCSSVariable(name) {
	return getComputedStyle(document.body).getPropertyValue(name);
}

/**
 * Retrieve a CSS3 variable value from its name as an Hexadecimal
 * color string. The variable must be defined in the :root scope.
 *
 * @param {string} name - The CSS3 variable name, e.g.,
 *   "--line-chart-border-color"
 */
export function getHexCSSVariable(name) {
	const getAttribute = (attribute) =>
		getCSSVariable(`${name}-${attribute}`).replace("%", "").trim();

	const h = getAttribute("h");
	const s = getAttribute("s");
	const l = getAttribute("l");

	if (!h || !s || !l) return null;

	return hslToHex(h, s, l);
}

/**
 * Converts hue, saturation and lightness to hexadecimal color. This
 * uses a simplification of the usual polygonal functions: See
 * https://en.wikipedia.org/wiki/HSL_and_HSV#HSL_to_RGB_alternative
 * for details about the implementation.
 */
export function hslToHex(hue, saturation, lightness) {
	lightness /= 100;
	const primaryRed = 0;
	const primaryGreen = 8;
	const primaryBlue = 4;

	const intensity = saturation * Math.min(lightness, 1 - lightness);
	const quotient = Math.round(intensity) / 100;

	const transform = (primary) => {
		const shifter = (primary + hue / 30) % 12;
		const shift = Math.min(shifter - 3, 9 - shifter, 1);
		const color = lightness - quotient * Math.max(shift, -1);
		const hex = Math.round(255 * color);
		return hex.toString(16).padStart(2, "0");
	};

	const red = transform(primaryRed);
	const green = transform(primaryGreen);
	const blue = transform(primaryBlue);

	return `#${red}${green}${blue}`;
}

/**
 * Parse a string to extract the value from a CSS3 var with a pixel unit.
 *
 * @param {string} string - String to parse
 * @return {number}
 */
export function pixelToValue(string) {
	return +string.replace("px", "");
}
